import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Col, Row, Menu, Table, Card, Tag, Divider, Typography, Space, Button, Modal } from 'antd'
import {
  ClockCircleTwoTone,
  RocketTwoTone,
  CloseCircleTwoTone,
  CheckCircleTwoTone,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import moment from 'moment'

import { apiToReadable } from '../../../common/utils/helpers'
import {
  fetchInspectionSubmissions,
  resetInspectionSubmissions,
} from '../../../store/reducers/inspection/inspectionSlice'

import {
  selectInspectionSubmissionsData,
  selectIsInspectionSubmissionsLoading,
  selectInspectionSubmissionById,
} from '../../../store/reducers/inspection/inspectionSelectors'

import Loader from '../../../common/layout/components/Loader'

const { Title, Text } = Typography

const GREEN = '#28a745'
const YELLOW = '#ffc107'
const RED = '#dc3545'

const getMenuItemIcon = (submissionStatus) => {
  const map = {
    pending: (
      <ClockCircleTwoTone twoToneColor={YELLOW} />
    ),
    in_progress: (
      <RocketTwoTone twoToneColor={YELLOW} />
    ),
    failed: (
      <CloseCircleTwoTone twoToneColor={RED} />
    ),
    complete: (
      <CheckCircleTwoTone twoToneColor={GREEN} />
    ),
  }
  return map[submissionStatus] || null
}

const InspectionSubmissionsView = ({ inspectionId }) => {
  const dispatch = useDispatch()

  const [submissionId, setSubmissionId] = useState('')
  const [isRawDataModalVisible, setIsRawDataModalVisible] = useState(false)

  const inspectionSubmissionsData = useSelector(selectInspectionSubmissionsData)
  const isInspectionSubmissionsLoading = useSelector(selectIsInspectionSubmissionsLoading)

  const submission = useSelector(state => selectInspectionSubmissionById(state, submissionId))

  useEffect(() => {
    const fetchInspectionSubmissionsData = async () => {
      await dispatch(resetInspectionSubmissions())
      await dispatch(fetchInspectionSubmissions(inspectionId))
    }

    if (inspectionId) {
      fetchInspectionSubmissionsData()
    }
  }, [inspectionId, dispatch])

  useEffect(() => {
    if (inspectionSubmissionsData.length > 0) {
      setSubmissionId(inspectionSubmissionsData[0]._id)
    }
  }, [inspectionSubmissionsData])

  const renderSubmissions = () => {
    if (isInspectionSubmissionsLoading) {
      return (
        <>
          <Loader />
        </>
      )
    } else if (inspectionSubmissionsData.length === 0) {
      return (
        <>
          <p style={{ textAlign: 'center' }}>No submissions.</p>
        </>
      )
    }

    const getTypeName = (submission_type) => {
      switch (submission_type) {
        case 'fannie':
          return '(Fannie)'
        case 'freddie':
          return '(Freddie)'
        default:
          return ''
      }
    }
    return (
      <Menu
        selectedKeys={[submissionId]}
        style={{ height: '100%', border: 'none' }}
        mode="vertical"
      >
        {inspectionSubmissionsData.map((submission, index) => (
          <Menu.Item
            key={submission._id}
            onClick={() => setSubmissionId(submission._id)}
            icon={getMenuItemIcon(submission.status)}
          >
            {`Submission ${getTypeName(submission.type)}  ${inspectionSubmissionsData.length - index}`}
          </Menu.Item>
        ))}
      </Menu>
    )
  }

  const renderSubmission = () => {
    if (isInspectionSubmissionsLoading) {
      return (
        <>
          <Loader />
        </>
      )
    } else if (!submission) {
      return null
    }

    const submissionData = [
      {
        key: 'type',
        label: 'Type',
        value: apiToReadable(submission.type),
      },
      {
        key: 'status',
        label: 'Status',
        value: (
          <Tag color={submission.status === 'complete' ? 'green' : submission.status === 'failed' ? 'red' : 'orange'}>
            {apiToReadable(submission.status)}
          </Tag>
        ),
      },
      {
        key: 'integration_id',
        label: 'Integration ID',
        value: submission.integration_id || 'N/A',
      },
      {
        key: 'created',
        label: 'Created',
        value: moment(submission.created).format('MM/DD/YYYY HH:mm'),
      },
    ]

    const errorColumns = [
      {
        title: 'Error Type',
        dataIndex: 'type',
        key: 'type',
        render: (type) => <Text code>{apiToReadable(type)}</Text>,
      },
      {
        title: 'Message',
        dataIndex: 'error_message',
        key: 'error_message',
        render: (message) => message || 'No message provided',
      },
      {
        title: 'Validation Errors',
        dataIndex: 'response',
        key: 'validation_errors',
        render: (response) => {
          if (response?.validationErrors?.length > 0) {
            return (
              <Space direction="vertical" size="small">
                {response.validationErrors.map((error, index) => (
                  <Text key={index} type="danger" style={{ fontSize: '12px' }}>
                    {error.jsonPath}: {error.validationErrorMessage}
                  </Text>
                ))}
              </Space>
            )
          }
          return <Text type="secondary">None</Text>
        },
      },
    ]

    return (
      <Card title="GSE Submission" style={{ marginBottom: '16px' }}>
        <Table
          dataSource={submissionData}
          pagination={false}
          showHeader={false}
          size="small"
          columns={[
            {
              dataIndex: 'label',
              key: 'label',
              width: '30%',
              render: (text) => <Text strong>{text}:</Text>,
            },
            {
              dataIndex: 'value',
              key: 'value',
            },
          ]}
        />

        {submission.errors?.length > 0 && (
          <>
            <Divider />
            <Title level={5} style={{ marginBottom: '12px' }}>
              <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: '8px' }} />
              Submission Errors
            </Title>
            <Table
              dataSource={submission.errors}
              columns={errorColumns}
              pagination={false}
              size="small"
              style={{ marginBottom: '16px' }}
            />
          </>
        )}
      </Card>
    )
  }

  const renderSubmissionStepsData = () => {
    const { submission_steps_data = {} } = submission || {}
    const lastEntry = Object.entries(submission_steps_data).pop()
    if (!lastEntry) {
      return null
    }

    const [lastStepName, lastStepData] = lastEntry
    const { success, errors, response_data } = lastStepData

    const stepData = [
      {
        key: 'step_name',
        label: 'Step Name',
        value: apiToReadable(lastStepName),
      },
      {
        key: 'success',
        label: 'Success',
        value: (
          <Space>
            {success ? (
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            ) : (
              <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
            )}
            <Text type={success ? 'success' : 'danger'}>
              {success ? 'Yes' : 'No'}
            </Text>
          </Space>
        ),
      },
    ]

    if (response_data?.status) {
      stepData.push({
        key: 'status',
        label: 'Status',
        value: (
          <Tag color={response_data.status === 'COMPLETE' ? 'green' : response_data.status === 'INCOMPLETE' ? 'orange' : 'default'}>
            {apiToReadable(response_data.status)}
          </Tag>
        ),
      })
    }

    const errorColumns = [
      {
        title: 'Error Type',
        dataIndex: 'type',
        key: 'type',
        render: (type) => <Text code>{apiToReadable(type)}</Text>,
      },
      {
        title: 'Message',
        dataIndex: 'error_message',
        key: 'error_message',
        render: (message) => message || 'No message provided',
      },
    ]

    const validationErrorColumns = [
      {
        title: 'JSON Path',
        dataIndex: 'jsonPath',
        key: 'jsonPath',
        render: (path) => <Text code>{path}</Text>,
      },
      {
        title: 'Error Message',
        dataIndex: 'validationErrorMessage',
        key: 'validationErrorMessage',
      },
    ]

    const imageColumns = [
      {
        title: 'Image Type',
        dataIndex: 'imageType',
        key: 'imageType',
        render: (type) => <Text code>{apiToReadable(type)}</Text>,
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        render: (desc) => desc || 'No description',
      },
      {
        title: 'Always Required',
        dataIndex: 'alwaysRequired',
        key: 'alwaysRequired',
        render: (required) => (
          <Tag color={required ? 'red' : 'default'}>
            {required ? 'Yes' : 'No'}
          </Tag>
        ),
      },
    ]

    const messageColumns = [
      {
        title: 'Code',
        dataIndex: 'messageCode',
        key: 'messageCode',
        render: (code) => <Text code>{code}</Text>,
        width: '20%',
      },
      {
        title: 'Description',
        dataIndex: 'messageDescription',
        key: 'messageDescription',
      },
    ]

    return (
      <Card
        title="Last Step Details"
        style={{ marginBottom: '16px' }}
        extra={
          <Button
            type="default"
            size="small"
            onClick={() => setIsRawDataModalVisible(true)}
          >
            View Raw Step Data
          </Button>
        }
      >
        <Table
          dataSource={stepData}
          pagination={false}
          showHeader={false}
          size="small"
          columns={[
            {
              dataIndex: 'label',
              key: 'label',
              width: '30%',
              render: (text) => <Text strong>{text}:</Text>,
            },
            {
              dataIndex: 'value',
              key: 'value',
            },
          ]}
        />

        {errors && errors.length > 0 && (
          <>
            <Divider />
            <Title level={5} style={{ marginBottom: '12px' }}>
              <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: '8px' }} />
              Step Errors
            </Title>
            <Table
              dataSource={errors}
              columns={errorColumns}
              pagination={false}
              size="small"
              style={{ marginBottom: '16px' }}
            />
          </>
        )}

        {response_data && (
          <>
            <Divider />
            <Title level={5} style={{ marginBottom: '12px' }}>
              Response Data
            </Title>

            {response_data.validationErrors && response_data.validationErrors.length > 0 && (
              <>
                <Title level={6} style={{ marginBottom: '8px' }}>Validation Errors</Title>
                <Table
                  dataSource={response_data.validationErrors}
                  columns={validationErrorColumns}
                  pagination={false}
                  size="small"
                  style={{ marginBottom: '16px' }}
                />
              </>
            )}

            {response_data.requiredImages && response_data.requiredImages.length > 0 && (
              <>
                <Title level={6} style={{ marginBottom: '8px' }}>Required Images</Title>
                <Table
                  dataSource={response_data.requiredImages}
                  columns={imageColumns}
                  pagination={false}
                  size="small"
                  style={{ marginBottom: '16px' }}
                />
              </>
            )}

            {response_data.missingImageFiles && response_data.missingImageFiles.length > 0 && (
              <>
                <Title level={6} style={{ marginBottom: '8px' }}>Missing Image Files</Title>
                <Table
                  dataSource={response_data.missingImageFiles}
                  columns={imageColumns}
                  pagination={false}
                  size="small"
                  style={{ marginBottom: '16px' }}
                />
              </>
            )}

            {response_data.messages && response_data.messages.length > 0 && (
              <>
                <Title level={6} style={{ marginBottom: '8px' }}>Messages</Title>
                <Table
                  dataSource={response_data.messages}
                  columns={messageColumns}
                  pagination={false}
                  size="small"
                />
              </>
            )}
          </>
        )}
      </Card>
    )
  }

  const renderRawDataModal = () => {
    const { submission_steps_data = {} } = submission || {}
    const lastEntry = Object.entries(submission_steps_data).pop()

    const rawData = lastEntry ? {
      stepName: lastEntry[0],
      stepData: lastEntry[1],
    } : {
      stepName: 'No step data available',
      stepData: null,
    }

    return (
      <Modal
        title={`Raw Step Data: ${lastEntry ? apiToReadable(lastEntry[0]) : 'No Data'}`}
        visible={isRawDataModalVisible}
        onCancel={() => {
          setIsRawDataModalVisible(false)
        }}
        footer={[
          <Button key="close" onClick={() => {
            setIsRawDataModalVisible(false)
          }}>
            Close
          </Button>,
        ]}
        width={800}
        style={{ top: 20 }}
      >
        <pre style={{
          background: '#f5f5f5',
          padding: '16px',
          borderRadius: '4px',
          maxHeight: '60vh',
          overflow: 'auto',
          fontSize: '12px',
          lineHeight: '1.4',
        }}>
          {JSON.stringify(rawData, null, 2)}
        </pre>
      </Modal>
    )
  }

  return (
    <div style={{ padding: '16px' }}>
      <Row gutter={[16, 16]}>
        <Col span={6}>
          <Card title="GSE Submissions" style={{ height: '100%' }}>
            {renderSubmissions()}
          </Card>
        </Col>
        <Col span={18}>
          {renderSubmission()}
          {submission && renderSubmissionStepsData()}
        </Col>
      </Row>
      {renderRawDataModal()}
    </div>
  )
}

export default InspectionSubmissionsView
